import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle2, Coffee, Users, Clock, ShieldCheck, PenTool, Wrench, Settings } from 'lucide-react';
export function Subway() {
  return <div className="pt-24 bg-white" data-id="element-898">
      {/* Top Summary */}
      <section className="py-16 md:py-24 bg-primary-light" data-id="element-899">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-900">
          <div className="max-w-3xl" data-id="element-901">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} className="inline-flex items-center space-x-2 bg-white px-3 py-1 rounded-full text-sm font-medium text-primary-dark mb-6" data-id="element-902">
              <Coffee className="w-4 h-4 text-accent" data-id="element-903" />
              <span data-id="element-904">Hospitality / Quick Service</span>
            </motion.div>
            <motion.h1 initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.1
          }} className="text-4xl md:text-5xl font-bold text-primary-dark mb-6" data-id="element-905">
              Reliable cooling for a busy customer-facing food service
              environment
            </motion.h1>
            <motion.p initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.2
          }} className="text-xl text-gray-700" data-id="element-906">
              Commercial climate solution designed to support customer comfort,
              staff working conditions and day-to-day reliability.
            </motion.p>
          </div>
        </div>
      </section>

      {/* Quick Facts Row */}
      <section className="py-12 border-b border-gray-200" data-id="element-907">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-908">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6" data-id="element-909">
            {[{
            label: 'Sector',
            value: 'Hospitality'
          }, {
            label: 'Service',
            value: 'Installation & Maintenance'
          }, {
            label: 'System',
            value: 'Cassette Systems'
          }, {
            label: 'Site',
            value: 'Quick Service Restaurant'
          }, {
            label: 'Maintenance',
            value: 'PPM Contract'
          }, {
            label: 'Location',
            value: 'UK Multiple Sites'
          }].map((fact, index) => <div key={index} className="bg-surface p-4 rounded-2xl" data-id="element-910">
                <p className="text-sm text-gray-500 mb-1" data-id="element-911">{fact.label}</p>
                <p className="font-semibold text-primary-dark" data-id="element-912">{fact.value}</p>
              </div>)}
          </div>
        </div>
      </section>

      {/* Challenge & Approach */}
      <section className="py-16 md:py-24" data-id="element-913">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-914">
          <div className="grid md:grid-cols-2 gap-16" data-id="element-915">
            <div data-id="element-916">
              <h2 className="text-3xl font-bold text-primary-dark mb-6" data-id="element-917">
                The Challenge
              </h2>
              <p className="text-gray-700 text-lg leading-relaxed" data-id="element-918">
                In a busy quick-service restaurant environment, maintaining a
                comfortable temperature is critical for both customer experience
                and staff wellbeing. The client was experiencing frequent
                breakdowns with their legacy cooling systems, leading to
                uncomfortable dining conditions during peak hours and
                challenging working environments in the food preparation areas.
                They needed a reliable, fast-turnaround solution.
              </p>
            </div>
            <div data-id="element-919">
              <h2 className="text-3xl font-bold text-primary-dark mb-6" data-id="element-920">
                Our Approach
              </h2>
              <ul className="space-y-4" data-id="element-921">
                {['Rapid site survey to assess immediate cooling needs', 'System specification tailored for high-footfall food service', 'Selection of robust, easy-to-clean cassette units', 'Out-of-hours installation to ensure zero disruption to trading', 'Implementation of a comprehensive preventative maintenance plan'].map((item, index) => <li key={index} className="flex items-start" data-id="element-922">
                    <CheckCircle2 className="w-6 h-6 text-accent flex-shrink-0 mt-1 mr-3" data-id="element-923" />
                    <span className="text-gray-700 text-lg" data-id="element-924">{item}</span>
                  </li>)}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* The Result */}
      <section className="py-16 md:py-24 bg-surface" data-id="element-925">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-926">
          <h2 className="text-3xl font-bold text-primary-dark mb-12 text-center" data-id="element-927">
            The Result
          </h2>
          <div className="grid md:grid-cols-3 gap-8" data-id="element-928">
            {[{
            icon: Users,
            title: 'Customer Comfort',
            desc: 'Consistently pleasant dining environment, encouraging longer stays and repeat visits.'
          }, {
            icon: ShieldCheck,
            title: 'Staff Conditions',
            desc: 'Significantly improved working conditions in the fast-paced preparation areas.'
          }, {
            icon: Clock,
            title: 'Day-to-day Reliability',
            desc: 'Drastic reduction in system downtime thanks to robust new equipment and proactive maintenance.'
          }].map((result, index) => <div key={index} className="bg-white p-8 rounded-3xl shadow-sm" data-id="element-929">
                <div className="w-12 h-12 bg-primary-light rounded-xl flex items-center justify-center mb-6" data-id="element-930">
                  <result.icon className="w-6 h-6 text-accent" data-id="element-931" />
                </div>
                <h3 className="text-xl font-bold text-primary-dark mb-3" data-id="element-932">
                  {result.title}
                </h3>
                <p className="text-gray-600" data-id="element-933">{result.desc}</p>
              </div>)}
          </div>
        </div>
      </section>

      {/* Client Quote */}
      <section className="py-24 bg-primary-dark text-white" data-id="element-934">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-935">
          <svg className="w-12 h-12 text-accent mx-auto mb-8 opacity-50" fill="currentColor" viewBox="0 0 24 24" data-id="element-936">
            <path d="M14.017 21v-7.391c0-5.704 3.731-9.57 8.983-10.609l.995 2.151c-2.432.917-3.995 3.638-3.995 5.849h4v10h-9.983zm-14.017 0v-7.391c0-5.704 3.748-9.57 9-10.609l.996 2.151c-2.433.917-3.996 3.638-3.996 5.849h3.983v10h-9.983z" data-id="element-937" />
          </svg>
          <blockquote className="text-2xl md:text-3xl font-medium leading-relaxed mb-8" data-id="element-938">
            "AccuTemp's in-house team resolved our cooling issues in days, not
            the weeks we'd been quoted elsewhere. Their service reports are the
            most thorough we've seen."
          </blockquote>
          <cite className="block text-lg text-gray-300 not-italic" data-id="element-939">
            — James Mitchell, Facilities Manager
          </cite>
        </div>
      </section>

      {/* Visual Proof */}
      <section className="py-16 md:py-24" data-id="element-940">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-941">
          <div className="rounded-3xl overflow-hidden aspect-video relative" data-id="element-942">
            <img src="https://cdn.magicpatterns.com/uploads/6woGoZZ58PZurSZ7Y9FmxV/subway-welcome.jpg" alt="Subway restaurant interior" className="w-full h-full object-cover" data-id="element-943" />
          </div>
        </div>
      </section>

      {/* Related Services */}
      <section className="py-16 bg-surface" data-id="element-944">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-945">
          <h2 className="text-2xl font-bold text-primary-dark mb-8" data-id="element-946">
            Related Services
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-id="element-947">
            {[{
            title: 'Installation',
            icon: PenTool,
            link: '/services/installation'
          }, {
            title: 'Maintenance',
            icon: Wrench,
            link: '/services/maintenance'
          }, {
            title: 'TM44 Compliance',
            icon: ShieldCheck,
            link: '/services/tm44'
          }, {
            title: 'AC support for FM & property teams',
            icon: Settings,
            link: '/services/maintenance'
          }].map((service, index) => <Link key={index} to={service.link} className="bg-white p-6 rounded-2xl hover:shadow-md transition-shadow group flex flex-col items-center text-center" data-id="element-948">
                <service.icon className="w-8 h-8 text-accent mb-4 group-hover:scale-110 transition-transform" data-id="element-949" />
                <span className="font-semibold text-primary-dark" data-id="element-950">
                  {service.title}
                </span>
              </Link>)}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 bg-primary-dark text-white text-center" data-id="element-951">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-952">
          <h2 className="text-3xl md:text-4xl font-bold mb-6" data-id="element-953">
            Planning a similar project?
          </h2>
          <p className="text-xl text-gray-300 mb-10" data-id="element-954">
            Our commercial team is ready to discuss your requirements and design
            a bespoke solution for your facility.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4" data-id="element-955">
            <Link to="/contact" className="inline-flex items-center justify-center px-8 py-4 text-lg font-bold rounded-full text-white bg-accent hover:bg-accent/90 transition-colors" data-id="element-956">
              Book a site survey
              <ArrowRight className="ml-2 w-5 h-5" data-id="element-957" />
            </Link>
            <Link to="/contact" className="inline-flex items-center justify-center px-8 py-4 text-lg font-bold rounded-full text-white border-2 border-white/20 hover:bg-white/10 transition-colors" data-id="element-958">
              Discuss your project
            </Link>
          </div>
        </div>
      </section>
    </div>;
}